export interface WalkthroughStep {
  id: number
  title: string
  description: string
  tip: string
  mockupType: string
}

export interface DFMPainPoint {
  title: string
  description: string
  icon: 'papers' | 'clock' | 'shield' | 'notes'
}

export interface DFMFeature {
  title: string
  description: string
  icon: 'mic' | 'ramble' | 'document' | 'workflow'
  walkthroughSteps: WalkthroughStep[]
}

export interface DFMDocumentTemplate {
  title: string
}

export interface DFMStep {
  number: number
  title: string
  description: string
}

export const painPoints: DFMPainPoint[] = [
  {
    title: 'Drowning in IC paper preparation',
    description:
      'Investment committee papers take hours to draft, pulling you away from the research and analysis that actually adds value.',
    icon: 'papers',
  },
  {
    title: 'Manual quarterly report writing',
    description:
      'Every quarter brings the same grind - compiling performance data, writing commentary, and formatting reports for each client.',
    icon: 'clock',
  },
  {
    title: 'Compliance documentation overhead',
    description:
      'Regulatory requirements mean endless paperwork - suitability assessments, due diligence records, and audit trails that consume your week.',
    icon: 'shield',
  },
  {
    title: 'Meeting notes scattered across systems',
    description:
      'IC meetings, client reviews, and strategy discussions happen constantly - but the notes end up in emails, notebooks, and shared drives with no structure.',
    icon: 'notes',
  },
]

export const features: DFMFeature[] = [
  {
    title: 'Meeting Capture & Transcription',
    description:
      'Record IC meetings, client reviews, and strategy discussions. AI produces structured minutes automatically - no more frantic note-taking.',
    icon: 'mic',
    walkthroughSteps: [
      {
        id: 1,
        title: 'Start your meeting recording',
        description:
          'Hit record as your IC meeting or client review begins. WealthSpace captures audio directly — whether in the boardroom, on a video call, or during a strategy session.',
        tip: 'Works with Zoom, Teams, and in-person meetings. No special hardware needed.',
        mockupType: 'mic-setup',
      },
      {
        id: 2,
        title: 'AI listens and transcribes in real-time',
        description:
          'While you focus on the discussion, WealthSpace transcribes and identifies key topics like portfolio decisions, risk assessments, and action items.',
        tip: 'The AI recognises investment-specific terminology and structures notes around your process.',
        mockupType: 'recording',
      },
      {
        id: 3,
        title: 'Review structured meeting minutes',
        description:
          'After the meeting, you get organised minutes — decisions made, rationale discussed, action items, and voting outcomes. All searchable and editable.',
        tip: 'Perfect for IC meetings — automatically captures who said what and what was agreed.',
        mockupType: 'notes-ready',
      },
      {
        id: 4,
        title: 'Generate documents from your minutes',
        description:
          'Turn meeting minutes into formal IC papers, client reports, or compliance documentation with one click. The AI uses your meeting context for accuracy.',
        tip: "The more you use WealthSpace, the better it learns your firm's style and conventions.",
        mockupType: 'document-export',
      },
    ],
  },
  {
    title: 'Ramble-to-Document',
    description:
      'Dictate investment thoughts and rationale. AI transforms rambles into structured IC papers, research notes, or client communications.',
    icon: 'ramble',
    walkthroughSteps: [
      {
        id: 1,
        title: 'Open ramble mode',
        description:
          'Tap the ramble button and start speaking. Describe your investment rationale, market views, or portfolio decisions — whatever you would normally write up.',
        tip: "Great for fund managers — dictate your investment thesis while it's fresh in your mind.",
        mockupType: 'ramble-start',
      },
      {
        id: 2,
        title: 'Speak freely and naturally',
        description:
          'Talk through your thinking as you would explain it to the IC. Mention the funds, allocations, risk factors, and your reasoning. No structure needed.',
        tip: "Don't worry about grammar or order — the AI handles all of that for you.",
        mockupType: 'recording',
      },
      {
        id: 3,
        title: 'AI transforms your words',
        description:
          'WealthSpace analyses your dictation and restructures it into a properly formatted document — IC paper, research note, or market commentary.',
        tip: "You can specify the output type: 'Make this an IC paper' or 'Create a research note'.",
        mockupType: 'processing',
      },
      {
        id: 4,
        title: 'Review and distribute',
        description:
          'Edit the polished draft, make any adjustments, and distribute to your adviser network or investment committee. Full audit trail maintained.',
        tip: 'Every document keeps an audit trail back to the original recording.',
        mockupType: 'document-export',
      },
    ],
  },
  {
    title: 'Document Generation',
    description:
      'AI-powered templates tailored to investment management workflows. Generate IC papers, quarterly reports, and client communications in minutes.',
    icon: 'document',
    walkthroughSteps: [
      {
        id: 1,
        title: 'Choose your template',
        description:
          'Browse investment management templates: IC papers, quarterly reports, fund factsheets, due diligence reports, market commentary, and more.',
        tip: 'Favourite your most-used templates for quick access from the dashboard.',
        mockupType: 'template-grid',
      },
      {
        id: 2,
        title: 'Select context and data',
        description:
          'Pick the portfolio, fund, or client. WealthSpace pulls in performance data, holdings, and recent meeting notes to pre-fill the document with accurate context.',
        tip: 'Data is auto-populated from your connected systems — no manual entry required.',
        mockupType: 'client-select',
      },
      {
        id: 3,
        title: 'AI generates your draft',
        description:
          'WealthSpace combines the template, portfolio data, and your commentary to produce a comprehensive first draft. Tailored to investment management standards.',
        tip: 'Generation typically takes under 30 seconds, even for detailed quarterly reports.',
        mockupType: 'processing',
      },
      {
        id: 4,
        title: 'Review, customise, and distribute',
        description:
          'Fine-tune the AI draft with the built-in editor. Adjust commentary, verify figures, then export as PDF or distribute to advisers and clients.',
        tip: 'All generated documents are stored with version history and audit trails.',
        mockupType: 'editor',
      },
    ],
  },
  {
    title: 'Workflow Management',
    description:
      'Track document approvals, review cycles, and compliance deadlines. Never miss a step in your investment process.',
    icon: 'workflow',
    walkthroughSteps: [
      {
        id: 1,
        title: 'View your workflow dashboard',
        description:
          'See all active workstreams at a glance — IC papers in progress, reports due, compliance reviews pending. Know the status of everything in your team.',
        tip: 'Filter by type, deadline, or team member to focus on priorities.',
        mockupType: 'pipeline',
      },
      {
        id: 2,
        title: 'Track deadlines and deliverables',
        description:
          'Never miss a quarterly report deadline, IC submission date, or client review. WealthSpace tracks every deliverable across your entire operation.',
        tip: 'Set up reminders to ensure nothing slips through the cracks.',
        mockupType: 'deadlines',
      },
      {
        id: 3,
        title: 'Manage approval workflows',
        description:
          "Route documents through review and approval chains. Track who's reviewed, who's approved, and what's outstanding at every stage.",
        tip: "Customise approval chains to match your firm's governance process.",
        mockupType: 'compliance',
      },
      {
        id: 4,
        title: 'Monitor team productivity',
        description:
          'Track document volumes, turnaround times, and team workload. Identify bottlenecks before they become problems with clear, actionable metrics.',
        tip: 'Export productivity reports for management or board meetings.',
        mockupType: 'analytics',
      },
    ],
  },
]

export const documentTemplates: DFMDocumentTemplate[] = [
  { title: 'Investment Committee (IC) Papers & Minutes' },
  { title: 'Quarterly Investment Reports' },
  { title: 'Model Portfolio Factsheets' },
  { title: 'Client Suitability Reports' },
  { title: 'Due Diligence Reports' },
  { title: 'Rebalancing Documentation' },
  { title: 'Market Commentary' },
  { title: 'Fund Selection Reports' },
  { title: 'Risk Assessment Documents' },
  { title: 'ESG / Responsible Investment Reports' },
  { title: 'Annual Management Reports' },
  { title: 'Client Review Meeting Notes' },
]

export const steps: DFMStep[] = [
  {
    number: 1,
    title: 'Record or ramble your investment thoughts',
    description:
      'Use meeting capture for IC discussions or simply dictate your investment rationale, market views, and portfolio decisions.',
  },
  {
    number: 2,
    title: 'AI structures and drafts your documents',
    description:
      'WealthSpace transforms your input into polished IC papers, quarterly reports, client communications, and compliance documentation.',
  },
  {
    number: 3,
    title: 'Review, tailor, and approve',
    description:
      'Edit AI-generated drafts with full control. Refine language, add specifics, and ensure everything meets your standards.',
  },
  {
    number: 4,
    title: 'Distribute to advisers and clients',
    description:
      'Share finalised documents with your adviser network and clients. Maintain a complete audit trail of every communication.',
  },
]
